import React, { useState, useEffect } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from 'moment';
import { ContentTitle } from '../ui/styled';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  PARTICIPANTS_URL,
  dateFormat,
  ADD_PARTICIPANT_FORM_URL,
  paginationSize,
} from '../../constants';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { participant } from '../../redux/participant/participantSlice';
import BackIcon from '../common/Back/BackIcon';
import PageHeading from '../common/Heading/PageHeading';
import { getParticipantsReferenceData } from '../../redux/participantsReferenceData/ParticipantsReferenceDataSlice';
import { commonQueryParams } from '../../services/QueryParams/QueryParams';
import CommonSearch from '../common/Search/CommonSearch';
import { getNewEpisodeReferenceData } from '../../redux/participant/newEpisode/NewEpisodeReference';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import ParticipantDialog from './ParticipantDialog';
import { LeftGrid } from '../Episode/Episode.Styled';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import {
  getAddParticipantFormSlice,
  newParticipant,
} from '../../redux/participant/AddParticipant/addParticipantSliceForm';
import { getParticipantsList } from './services/participantsApi';
import { renderCellExpand } from '../common/GridCell/GridCellExpand';

const Participant = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  const [user, setUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [data, setData] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
    sortModel: '',
  });

  const participantRowClick = (rowData) => {
    //console.log('### CLICK FROM PARTICIPANT LIST');
    //console.log(rowData);

    const participantDetail = [
      {
        key: rowData.key,
        id: rowData.id,
        lastName: rowData.lastName,
        firstName: rowData.firstName,
        middleName: rowData.middleName,
        ssn: rowData.SSN,
        suffix: rowData.suffix,
        gender: rowData.gender,
        ethnicityOrRace: rowData.ethnicityOrRace,
        veteran: rowData.veteran,
        deployedCombatDuty: rowData.deployedCombatDuty,
        diagnosedPostTraumaticStressDisorder:
          rowData.diagnosedPostTraumaticStressDisorder,
        arsonist: rowData.arsonist,
        dateOfBirth: rowData.dateOfBirth,
        registration290: rowData.registration290,
        inactive: rowData.inactive,
        cdcNum: rowData.CDCNum
      },
    ];
    dispatch(participant(participantDetail));
    navigate(`/participants/details/${rowData.id}`);
  };

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchData();
    }
    getUser().then((userData) => setUser(userData));
  }, [
    user?.signInUserSession.accessToken.jwtToken,
    data.pageSize,
    data.page,
    data.sortModel,
    searchText,
  ]);

  const QuickSearchToolbar = () => {
    return (
      <CommonSearch searchText={searchText} setSearchText={setSearchText} />
    );
  };

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchReferenceData();
    }
  }, [user?.signInUserSession.accessToken.jwtToken]);

  const fetchReferenceData = async () => {
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    dispatch(getParticipantsReferenceData(authorization));
    dispatch(getNewEpisodeReferenceData(authorization));
  };

  const fetchData = async () => {
    setData((old) => ({ ...old, isLoading: true }));
    const commonParams = commonQueryParams(PARTICIPANTS_URL, searchText, data);
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    const participants = await getParticipantsList({
      commonParams,
      authorization,
    });
    setData((old) => ({
      ...old,
      ...participants,
    }));
  };

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  const handleSortModelChange = React.useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    setData((old) => ({ ...old, sortModel: [...sortModel] }));
  }, []);

  const updateParticipantDialog = (params) => {
    //console.log(params)
    const url = `${ADD_PARTICIPANT_FORM_URL}/participants/${params.row.id}`;
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    setOpenBackdrop(true);
    setTimeout(() => {
      setOpenBackdrop(false);
    }, 700);
    dispatch(getAddParticipantFormSlice({ url, authorization }));
    setOpen(true);
  };

  const openNewParticipant = () => {
    const addNewParticipant = {
      newLoadedData: true,
      lastName: '',
      firstName: '',
      middleName: '',
      SSN: '',
      dateOfBirth: '',
      suffix: '',
      gender: '',
      ethnicityOrRace: '',
      veteran: '',
      deployedCombatDuty: '',
      diagnosedPostTraumaticStressDisorder: '',
      registration290: '',
      arsonist: '',
    };
    dispatch(newParticipant(addNewParticipant));
    setOpen(true);
  };

  const columnsData = [
    {
      field: 'actions',
      headerName: 'Actions',
      //type: 'actions',
      renderCell: (params) => {
        return (
          <IconButton onClick={() => updateParticipantDialog(params)}>
            <EditIcon />
          </IconButton>
        );
      },
    },
    { 
      field: 'key', 
      headerName: 'Participant Key', 
      minWidth: 225,
      renderCell: (params) => {
        return (
          <Button
          variant="contained"
          size="small"
          onClick={() => participantRowClick(params.row)}
        >
          {params.value}
        </Button>  
        )
      }
    },
    //{ 
    //  field: 'CDCNum', 
    //  headerName: 
    //  'CDC Num', 
    //  flex: 1, 
    //  minWidth: 75,
    //},
    { field: 'id', headerName: 'Prt Id', flex: 1 },
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'middleName', headerName: 'Middle Name', flex: 1 },
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    //{ field: 'SSN', headerName: 'SSN', flex: 1 },
    {
      field: 'dateOfBirth',
      headerName: 'DOB',
      renderCell: (params) => {
        return (
          <Typography>{moment(params.value).format(dateFormat)}</Typography>
        );
      },
      flex: 1,
    },
    {
      field: 'arsonist',
      headerName: 'Arsonist',
      type: 'boolean',
      flex: 1,
    },
    {
      field: 'registration290',
      headerName: '290 Reg',
      type: 'boolean',
      flex: 1,
    },
    {
      field: 'inactive',
      headerName: 'Inactive',
      type: 'boolean',
      flex: 1,
    },
  ];

  return (
    <Box>
      <ContentTitle container>
        <Grid item xs={4}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <BackIcon />
            <PageHeading title='Participant List' />
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box style={{ alignItems: 'center' }}>
            <Typography variant='h4' align='center'>Participants</Typography>
          </Box>
        </Grid>
        <LeftGrid item xs={4}>
          <Button
            variant='contained'
            style={{ justifyContent: 'flex-end' }}
            onClick={() => openNewParticipant()}
          >
            New Participant
          </Button>
        </LeftGrid>
      </ContentTitle>
      <div style={{ height: 670, width: '100%' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          <div style={{ flexGrow: 1 }}>
            <DataGridPro
            initialState={{
                columns: {
                  columnVisibilityModel: {
                    id: false,
                  }},
              }}
              disableColumnFilter
              //autoHeight
              //getRowHeight={() => 'auto'}
              columns={columnsData}
              rows={data.data}
              page={data.page - 1}
              paginationMode='server'
              sortingMode='server'
              rowCount={data.total}
              rowsPerPageOptions={paginationSize}
              pageSize={data.pageSize}
              pagination
              components={{ Toolbar: QuickSearchToolbar }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              //onRowClick={(param) => participantRowClick(param.row)}
              onPageSizeChange={(newPageSize) =>
                setData((old) => ({ ...old, pageSize: newPageSize }))
              }
              onPageChange={(newPage) => {
                setData((old) => ({ ...old, page: newPage + 1 }));
              }}
              onSortModelChange={handleSortModelChange}
              loading={data.isLoading}
            />
          </div>
        </div>
      </div>      
      <ParticipantDialog
        open={open}
        setOpen={setOpen}
        openBackdrop={openBackdrop}
        fetchData={fetchData}
        authToken={user?.signInUserSession.accessToken.jwtToken}
      />
    </Box>
  );
};
export default Participant;
